import { Box } from '@mui/material'
import { FC, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useDrop } from 'react-dnd'

import { DraggableRow, RowProps } from '@/components/dnd-area/draggable-row.tsx'
import { DndCustomContext } from '@/components/dnd-area/dnd-custom-context.tsx'

const GAP_AREA_PX = 4

type DndAreaProps = {
  items: RowProps[]
  onReorder?: (newItems: RowProps[]) => void
}

export const DndArea: FC<DndAreaProps> = ({ items: initialItems, onReorder }) => {
  const [items, setItems] = useState(initialItems)
  const { setIsDragging, isTouchDevice, isDragging } = useContext(DndCustomContext)
  const ariaRef = useRef<HTMLDivElement>(null)
  const [virtualItems, setVirtualItems] = useState(initialItems)
  const [translatesY, setTranslatesY] = useState<Record<string, number>>(
    initialItems.reduce(
      (acc, e) => {
        acc[e.id] = 0
        return acc
      },
      {} as Record<string, number>
    )
  )

  const [, dropTarget] = useDrop({
    accept: 'row',
    drop: () => {
      setIsDragging(false)
    },
  })

  useEffect(() => {
    if (!isDragging) {
      setItems(virtualItems)
      setVirtualItems(virtualItems)
      setTranslatesY(
        virtualItems.reduce(
          (acc, e) => {
            acc[e.id] = 0
            return acc
          },
          {} as Record<string, number>
        )
      )
    }
  }, [isDragging])

  useEffect(() => {
    if (onReorder) {
      const newItems = virtualItems.map((e) => {
        const itemWithActualIsCheck = items.find((i) => i.id === e.id)
        if (!itemWithActualIsCheck) return e
        e.isChecked = itemWithActualIsCheck.isChecked
        return e
      })

      onReorder(newItems)
    }
  }, [items])

  useLayoutEffect(() => {
    setTranslatesY((prevTranslate) => {
      if (!ariaRef.current) return prevTranslate

      const newTranslates = { ...prevTranslate }

      for (const key in prevTranslate) {
        const indexItemFromNew = virtualItems.findIndex((e) => e.id === key)
        const indexItemFromPrev = Object.keys(prevTranslate).indexOf(key)

        const element = ariaRef.current.children.item(indexItemFromPrev)
        const heightElement = element ? element.getBoundingClientRect().height : 0

        newTranslates[key] = (indexItemFromNew - indexItemFromPrev) * (heightElement + GAP_AREA_PX)
      }

      return newTranslates
    })
  }, [virtualItems])

  const moveRow = (dragIndex: number, hoverIndex: number) => {
    setVirtualItems((prevItems) => {
      const newItems = [...prevItems]
      const [draggedItem] = newItems.splice(dragIndex, 1)
      newItems.splice(hoverIndex, 0, draggedItem)

      return newItems
    })
  }

  const getIndexFromVirtualItems = (id: string) => {
    const item = virtualItems.find((e) => e.id === id)
    if (item) {
      return virtualItems.indexOf(item)
    }

    return 0
  }

  const onCheckChange = (id: string, isCheck: boolean) => {
    setItems((prev) => {
      const newItems = [...prev]
      const item = newItems.find((e) => e.id === id)
      if (item) {
        item.isChecked = isCheck
        return newItems
      }

      return prev
    })
  }

  dropTarget(ariaRef)

  return (
    <Box
      data-testid="dnd-area-test"
      ref={ariaRef}
      sx={(theme) => ({
        borderRadius: '12px',
        padding: '10px 10px',
        gap: `${GAP_AREA_PX}px`,
        display: 'flex',
        justifyContent: 'flex-start',
        flexDirection: 'column',
        maxHeight: isTouchDevice ? 'auto' : '35vh',
        overflowY: 'scroll',
        border: `1px solid ${theme.palette.secondary.main}`,
        boxSizing: 'border-box',
      })}
    >
      {items.map((e) => (
        <DraggableRow
          index={getIndexFromVirtualItems(e.id)}
          key={e.id}
          moveRow={moveRow}
          translateY={translatesY[e.id]}
          onCheckChange={onCheckChange}
          {...e}
        />
      ))}
    </Box>
  )
}
